import { formatAsFloatString, formatAsPercentString } from './utils';
import * as RenewalStatus from '../constants/RenewalStatus';
import { getBranchRole } from './session-util';
import * as USER_TYPE from '../constants/UserType';
import { isNumber } from 'lodash-es';
import * as BillMethod from '../constants/BillMethod';
import { APPROVAL_LEVELS, INVENTORY_MAINTENANCE_TYPES } from './util-constants';
import { formatNumber } from './format-number-util';
import {
  PRICE_PRECISION,
  DB_PRICE_PRECISION
} from '../constants/decimal-points';

const MAX_PRICE_DECIMAL_NORMALIZER = 10000;

const validationMappings = {
  NO_RATES_PROVIDED: {
    message: 'no_rates_provided_message',
    category: 0
  },
  BELOW_RED_RATE: {
    message: 'below_red_rate_message',
    category: 0
  },
  TIER1_APPROVAL_REQUIRED: {
    message: 'price_approval_message',
    category: 0
  },
  TIER2_APPROVAL_REQUIRED: {
    message: 'price_approval_message',
    category: 0
  },
  TIER3_APPROVAL_REQUIRED: {
    message: 'price_approval_message',
    category: 0
  },
  DIFFERENT_TIER: {
    message: 'different_tier_message',
    category: 0
  },
  SERVICE_CHARGE_DECREASED: {
    message: 'service_charge_decreased',
    category: 2
  },
  PRICE_PROTECTION_NOT_QUALIFIED: {
    message: 'price_protection_not_qualified',
    category: 4
  },
  QUANTITY_DECREASED: {
    message: 'quantity_decreased',
    category: 1
  },
  RENEWAL_TERM_APPROVAL_NEEDED: {
    message: 'renewal_term_approval_needed',
    category: 3
  },
  GENERIC_VALIDATION_ERROR: {
    message: 'renewal_term_approval_needed',
    category: 5
  }
};

const createPriceLabel = (value, identifier = null) =>
  `${formatAsFloatString(value, PRICE_PRECISION)}${
    !!identifier ? ` (${identifier})` : ''
  }`;

const getPriceOptions = (
  originalPrice,
  adjustedPrice,
  basePriceIncrease = 0,
  showMadRate = false,
  madRate = null
) => {
  const pricingOptions = [];

  for (let i = basePriceIncrease * 100; i > 0; i--) {
    pricingOptions.push({
      value: Number(
        (originalPrice + (originalPrice * i) / 100).toFixed(DB_PRICE_PRECISION)
      ),
      labelId: `+${i}%`
    });
  }

  if (showMadRate) {
    pricingOptions.push({
      value: madRate != null ? madRate : originalPrice,
      labelId:
        madRate == null || originalPrice === madRate
          ? 'current_contract_rate_and_mad'
          : 'mad_rate'
    });
  }

  if (adjustedPrice !== originalPrice) {
    pricingOptions.push({
      value: adjustedPrice
    });
  }

  pricingOptions.push({
    value: originalPrice,
    labelId: 'current_contract_rate'
  });

  return pricingOptions;
};

const withoutDuplicates = (priceOptions) => {
  return priceOptions.reduce(
    (optionSet, option) =>
      !!optionSet.find(
        (uniqueOption) =>
          formatNumber(uniqueOption.value, PRICE_PRECISION) ===
          formatNumber(option.value, PRICE_PRECISION)
      )
        ? optionSet
        : optionSet.concat([option]),
    []
  );
};

const getRenewalPriceOptions = (
  originalPrice,
  adjustedPrice,
  basePriceIncrease = 0,
  showMadRate = false,
  madRate = null
) => {
  if (!isNumber(originalPrice) || !isNumber(adjustedPrice)) {
    return [];
  }

  const priceOptions = getPriceOptions(
    originalPrice,
    adjustedPrice,
    basePriceIncrease,
    showMadRate,
    madRate
  );
  return withoutDuplicates(priceOptions);
};

const billMethodById = (id) => BillMethod.getBillingMethodById(id);

const extractMessageFromValidation = (validation) => {
  return getValidationMapping(validation).message;
};

const getValidationMapping = (validation) => {
  const mapping = !!validationMappings[validation.message]
    ? validationMappings[validation.message]
    : validationMappings.GENERIC_VALIDATION_ERROR;
  return {
    ...mapping,
    validityLevel: validation.validityLevel
  };
};

const extractReasonFromValidation = (validation, reasonCodes = []) => {
  const reasonProvided = !!validation.adjustmentReason;
  const reasonCodeProvided =
    reasonProvided && !!validation.adjustmentReason.reasonId;
  const customReasonProvided =
    reasonProvided && !!validation.adjustmentReason.reason;

  let reasonText = '';
  if (reasonCodeProvided) {
    const reasonCode = reasonCodes.find(
      (reasonCode) =>
        reasonCode.reasonId === validation.adjustmentReason.reasonId
    );
    const reasonCodeLabel = !!reasonCode ? reasonCode.reason : '';

    reasonText += `: ${reasonCodeLabel}`;
  }
  if (customReasonProvided) {
    reasonText += ` - ${validation.adjustmentReason.reason}`;
  }

  return reasonText;
};

const isRenewalMutable = (renewal, branchCode) => {
if(!renewal) return false;
  const role = getBranchRole(branchCode);
  return (
    !!renewal &&
    renewal.status === RenewalStatus.IN_PROGRESS.name &&
    !!role &&
    role !== USER_TYPE.C3.name &&
    role !== USER_TYPE.CAM.name
  );
};

const getValidationMessages = (renewal, filterFunc = () => true) => {
  return [...renewal.validationErrors.filter(filterFunc)];
};

const itemNeedsApproval = (responses) =>
  !!responses && Array.isArray(responses) && responses.length > 0
    ? responses.filter(
        (response) =>
          response.validityLevel === APPROVAL_LEVELS.TIER3_APPROVAL || response.validityLevel === APPROVAL_LEVELS.TIER2_APPROVAL ||
          response.validityLevel === APPROVAL_LEVELS.TIER1_APPROVAL
      ).length > 0
    : null;

const getValidationFields = (validationErrors) => {
  return validationErrors.map((validationError) =>
    validationError.validationField.toUpperCase()
  );
};

const getApprovalTierNeeded = (reponses) => {
  if (
    reponses.some(
      (response) => response.validityLevel === APPROVAL_LEVELS.TIER3_APPROVAL
    )
  )
    return APPROVAL_LEVELS.TIER3_APPROVAL;
  else if (
    reponses.some(
      (response) => response.validityLevel === APPROVAL_LEVELS.TIER2_APPROVAL
    )
  )
    return APPROVAL_LEVELS.TIER2_APPROVAL;
  else if (
    reponses.some(
      (response) => response.validityLevel === APPROVAL_LEVELS.TIER1_APPROVAL
    )
  )
    return APPROVAL_LEVELS.TIER1_APPROVAL;
  else return APPROVAL_LEVELS.NONE;
};

const normalizeRenewalLineItems = (renewal, intl) => {
  const { lineItems } = renewal;

  const mrsProductCodes = lineItems
    .filter((lineItem) => lineItem.size === 'MRSEA')
    .map((lineItem) => lineItem.itemCode);

  if (mrsProductCodes.length > 0) {
    const normalizedLineItems = lineItems.filter(
      (lineItem) => !mrsProductCodes.includes(lineItem.itemCode)
    );

    const mrsItems = lineItems.filter(
      (lineItem) =>
        lineItem.size === 'MRSEA' &&
        ((lineItem.itemCode.startsWith('CH') &&
          lineItem.originalPrice.priceItem != null &&
          lineItem.baseQuantity.deliveryQuantity != null) ||
          (lineItem.itemCode.startsWith('CE') &&
            lineItem.originalPrice.priceRental != null &&
            lineItem.baseQuantity.rentalQuantity != null))
    );

    const salesPrice = {
      priceItem:
        mrsItems.length > 0
          ? mrsItems.reduce(
              (accumulator, currentValue) =>
                accumulator +
                (currentValue.itemCode.startsWith('CH')
                  ? currentValue.originalPrice.priceItem *
                    currentValue.baseQuantity.deliveryQuantity
                  : currentValue.originalPrice.priceRental *
                    currentValue.baseQuantity.rentalQuantity),
              0
            )
          : 0
    };

    const quantity = {
      deliveryQuantity: 1
    };

    normalizedLineItems.push({
      renewalLineId: 'MRSBUNDLE',
      lineItemId: 'MRSBUNDLE',
      adjustedPrice: salesPrice,
      adjustedQuantity: quantity,
      basePrice: salesPrice,
      baseQuantity: quantity,
      billMethod: BillMethod.PieceRateOnly,
      description: intl.formatMessage({
        id: 'restroom_services'
      }),
      itemCode: 'MRSBUNDLE',
      itemName: intl.formatMessage({
        id: 'restroom_services'
      }),
      itemNumber: 'MRSBUNDLE',
      originalPrice: salesPrice,
      renewalId: renewal.renewalId,
      typeOfMerchandise: 'SALES ONLY',
      size: 'MRSEA',
      isAdjustable: false,
      shouldBeSuppressed: false
    });
    return normalizedLineItems;
  }
  return lineItems;
};

const normalizeCustomerProducts = (customerProducts, intl) => {
  const mrsProductCodes = customerProducts
    .filter((customerProduct) => customerProduct.sizeCode === 'MRSEA')
    .map((customerProduct) => customerProduct.productCode);

  if (mrsProductCodes.length > 0) {
    const normalizedCustomerProducts = customerProducts.filter(
      (customerProduct) =>
        !mrsProductCodes.includes(customerProduct.productCode)
    );

    const mrsItems = customerProducts.filter(
      (customerProduct) =>
        customerProduct.sizeCode === 'MRSEA' &&
        ((customerProduct.productCode.startsWith('CH') &&
          customerProduct.salesPrice != null &&
          customerProduct.deliveryQuantity != null) ||
          (customerProduct.productCode.startsWith('CE') &&
            customerProduct.rentalPrice != null &&
            customerProduct.quantity != null))
    );

    const salesPrice =
      mrsItems.length > 0
        ? mrsItems.reduce(
            (accumulator, currentValue) =>
              accumulator +
              (currentValue.productCode.startsWith('CH')
                ? currentValue.salesPrice * currentValue.deliveryQuantity
                : currentValue.rentalPrice * currentValue.quantity),
            0
          )
        : 0;

    normalizedCustomerProducts.push({
      billingMethod: BillMethod.PieceRateOnlyId,
      productDescription: intl.formatMessage({
        id: 'restroom_services'
      }),
      productCode: 'MRSBUNDLE',
      salesPrice,
      deliveryQuantity: 1,
      prodGroupCode: 'MRSBUNDLE',
      typeOfMerchandise: 'SALES ONLY',
      shouldBeSuppressed: false
    });
    return normalizedCustomerProducts;
  }
  return customerProducts;
};

const filterOutSuppressedItems = (renewalLineItems) => {
  return renewalLineItems.filter(
    (lineItem) => lineItem.shouldBeSuppressed === false
  );
};

const getRenewalValidations = (renewal, priceReasonCodes, intl) => {
  return getValidationMessages(renewal).map((validation) => {
    const mapping = getValidationMapping(validation);
    const { category, message, validityLevel } = mapping;
    const reason = extractReasonFromValidation(validation, priceReasonCodes);

    const reasonMessage = `${intl.formatMessage({
      id: 'reason_given'
    })} ${reason}`;

    const matchingLineItem = !!validation.lineItemId
      ? renewal.lineItems.find(
          (lineItem) => lineItem.lineItemId === validation.lineItemId
        )
      : null;
    const validationMessage = `${
      !!matchingLineItem ? matchingLineItem.itemCode + ': ' : ''
    }${intl.formatMessage({
      id: message
    })}${reason.includes(`NO_REASON_REQUIRED`) ? '' : ` ${reasonMessage}`}`;

    return {
      itemCode: !!matchingLineItem ? matchingLineItem.itemCode : null,
      message: validationMessage,
      category,
      validityLevel
    };
  });
};

const getInventoryMaintenance = (value, type) => {
  if (
    value == null ||
    type == null ||
    type.toUpperCase() === INVENTORY_MAINTENANCE_TYPES.NO_SRC
  )
    return null;

  return type.toUpperCase() === INVENTORY_MAINTENANCE_TYPES.FIXED
    ? value
    : formatAsPercentString(value, false);
};

const showInventoryMaintenanceAsPercent = (type) =>
  type && type.toUpperCase() !== INVENTORY_MAINTENANCE_TYPES.FIXED;

const calculateDiscount = (base, adjusted) =>
  ((MAX_PRICE_DECIMAL_NORMALIZER * base -
    MAX_PRICE_DECIMAL_NORMALIZER * adjusted) /
    (MAX_PRICE_DECIMAL_NORMALIZER * (base !== 0 ? base : 1))) *
  100;

export {
  createPriceLabel,
  getRenewalPriceOptions,
  billMethodById,
  extractMessageFromValidation,
  extractReasonFromValidation,
  getRenewalValidations,
  isRenewalMutable,
  getValidationMessages,
  itemNeedsApproval,
  getValidationFields,
  getApprovalTierNeeded,
  normalizeRenewalLineItems,
  normalizeCustomerProducts,
  filterOutSuppressedItems,
  getInventoryMaintenance,
  showInventoryMaintenanceAsPercent,
  calculateDiscount
};
